package com.steamstreet.vegasful.html.components

import com.steamstreet.vegasful.css.*
import kotlinx.css.*
import kotlinx.html.FlowContent
import kotlinx.html.Tag
import kotlinx.html.div
import kotlinx.html.unsafe


/**
 * Render the main content area, which constrains the width to a maximum size.
 */
fun FlowContent.mainContentArea(block: FlowContent.() -> Unit) {
    div {
        css {
            maxWidth = VegasfulStyles.mainContentMaxWidth
            width = 100.pct
            zIndex = 60
            position = Position.relative
            margin = Margin(0.px)

            whenWiderThanPortraitPhone {
                margin = Margin(0.px, 20.px)
            }

            whenReallyWide {
                margin = Margin(0.px, LinearDimension.auto)
            }
        }
        div {
            block()
        }
    }
}

class SplitContentArea {
    internal var l: (FlowContent.() -> Unit)? = null
    internal var r: (FlowContent.() -> Unit)? = null

    fun left(block: FlowContent.() -> Unit) {
        l = block
    }

    fun right(block: FlowContent.() -> Unit) {
        r = block
    }
}

/**
 * Content area where there is a large block and a small block, which will appear on top of each
 * other in mobile.
 */
fun FlowContent.splitContentArea(block: SplitContentArea.() -> Unit) {
    val config = SplitContentArea().apply(block)
    mainContentArea {
        div {
            css {
                display = Display.grid
                // Use minmax(0, 1fr) rather than a bare 1fr so the track is allowed
                // to shrink below the min-content size of its contents. Without this,
                // a grid cell containing nowrap content (e.g. the horizontally
                // scrolling tag pills) or long unbroken text forces the track - and
                // therefore the whole page - wider than the viewport on mobile.
                gridTemplateColumns = GridTemplateColumns("minmax(0, 1fr)")


                whenWiderThanPortraitPhone {
                    marginTop = 10.px
                    gridTemplateColumns = GridTemplateColumns("minmax(0, 1fr) 35%")
                    gridAutoRows = GridAutoRows("1fr")
                    gap = 20.px
                }
            }

            div {
                // Grid items default to min-width: auto, which prevents them from
                // shrinking below their content's min-content width. Pin to 0 so the
                // cell stays within its track and its own overflow/wrapping applies.
                css {
                    minWidth = 0.px
                }
                config.l?.invoke(this)
            }
            div {
                css {
                    minWidth = 0.px
                }
                config.r?.invoke(this)
            }
        }
    }
}

/**
 * Renders a rich description in a div, typically on the left side of a split content area
 */
fun FlowContent.richDescription(description: String?, boldLinks: Boolean = false) {
    if (description == null) return

    div {
        css {
            padding = Padding(0.px, 10.px)
            whenWiderThanPortraitPhone {
                padding = Padding(0.px)
            }
            fontSize = (0.9).rem
            lineHeight(1.2.em)
            fontWeight = FontWeight.w400
            if (boldLinks) {
                kotlinx.css.a {
                    fontWeight = FontWeight.w700
                }
            }

            "*:first-child" {
                marginTop = 0.px
            }

            "*:last-child" {
                marginBottom = 0.px
            }

        }
        unsafe {
            +description
        }
    }
}

/**
 * Render text. Same as using the + operator, but aligns more with
 * Compose multiplatform
 */
fun Tag.Text(text: String?) {
    if (text == null) return
    +text
}